/**
 * Service status schema (#1341, program #1130 E3)
 *
 * Zod contract for GET /api/v1/dashboard/service — Settings → «Работа сервиса»:
 * how the organization's own records move through processing. The same
 * readers as `/dashboard` → `adminOps`, for the same audience (client, admin,
 * the platform owner; a reader narrower than the whole org gets none of it).
 *
 * This is the ORGANIZATION's service, not the platform's diagnostics: an
 * error reaches the customer as a reason from `PROCESSING_ERROR_REASONS`,
 * never as its text. The stored text is the pipeline's own — on production
 * it holds SQL statements, Durable Object internals and storage URLs — and it
 * is sent (`detail`) to the platform owner only.
 *
 * Every block answers for itself: one source failing is that block's
 * `error`, never a zero and never its neighbours' failure.
 */

import { z } from 'zod/v4';

// ============================================================================
// Query
// ============================================================================

/** The window of the period blocks; the queue is always «now». */
export const SERVICE_STATUS_PERIODS = ['7d', '30d'] as const;

export const serviceStatusQuerySchema = z.object({
  period: z.enum(SERVICE_STATUS_PERIODS).default('7d'),
});

// ============================================================================
// Error reasons
// ============================================================================

/**
 * Why a record stopped, in words the customer can act on. Exhaustive: an
 * error the classifier does not recognise is `internal` («сбой на нашей
 * стороне»), not a raw text.
 */
export const PROCESSING_ERROR_REASONS = [
  'no_recording',
  'media_timeout',
  'audio_unavailable',
  'transcription_failed',
  'analysis_failed',
  'delivery_failed',
  'internal',
] as const;

export const processingErrorReasonSchema = z.enum(PROCESSING_ERROR_REASONS);

// ============================================================================
// Response
// ============================================================================

const countSchema = z.number().int().nonnegative();

/** Rows the queue and the error table list; the rest is counted, not dropped silently. */
export const SERVICE_STATUS_MAX_ROWS = 10;

function block<T extends z.ZodType>(data: T) {
  return z.discriminatedUnion('status', [
    z.object({ status: z.literal('ok'), data }),
    z.object({ status: z.literal('error') }),
  ]);
}

const errorRowSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
  projectName: z.string().nullable(),
  updatedAt: z.string().nullable(),
  reason: processingErrorReasonSchema,
  /** The pipeline's own text — the platform owner only; absent for everyone else. */
  detail: z.string().nullable().optional(),
});

export const serviceStatusResponseSchema = z.object({
  period: z.object({
    key: z.enum(SERVICE_STATUS_PERIODS),
    start: z.string(),
    end: z.string(),
  }),
  asOf: z.string(),
  /** Records in processing right now — no period. */
  queue: block(
    z.object({
      total: countSchema,
      byStatus: z.array(z.object({ status: z.string(), count: countSchema })),
      oldestQueuedAt: z.string().nullable(),
    })
  ),
  /** Records created in the period, by where they ended up. */
  throughput: block(
    z.object({ total: countSchema, completed: countSchema, failed: countSchema })
  ),
  /** Completed stage runs of the period and their average duration. */
  stages: block(
    z.array(
      z.object({
        stage: z.string(),
        count: countSchema,
        avgSeconds: z.number().nonnegative(),
      })
    )
  ),
  /** Stage runs of the period, failed among them, and the latest failure's reason. */
  stageFailures: block(
    z.array(
      z.object({
        stage: z.string(),
        total: countSchema,
        failed: countSchema,
        lastReason: processingErrorReasonSchema.nullable(),
        lastFailedAt: z.string().nullable(),
      })
    )
  ),
  /** Failed records of the period by the source they came through. */
  errorsBySource: block(z.array(z.object({ source: z.string(), count: countSchema }))),
  /** The latest failed records of the period, at most `SERVICE_STATUS_MAX_ROWS`. */
  recentErrors: block(z.array(errorRowSchema)),
});

export type ServiceStatusQuery = z.infer<typeof serviceStatusQuerySchema>;
export type ProcessingErrorReason = z.infer<typeof processingErrorReasonSchema>;
export type ServiceStatusResponse = z.infer<typeof serviceStatusResponseSchema>;
